import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Btn, TextInput, SelectInput, Loader, EmptyState } from "../components/Primitives";

const MODULE_META = {
  company_profile: { label: "Company Profile", icon: "Building2", color: "#0891B2" },
  assessment: { label: "DPDP Assessment", icon: "ClipboardCheck", color: "#4F46E5" },
  vapt: { label: "VAPT", icon: "ShieldAlert", color: "#DC2626" },
  iso27001: { label: "ISO 27001", icon: "Award", color: "#059669" },
};

export default function EvidenceVault() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [module, setModule] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (module) params.set("module", module);
      const [{ data: v }, { data: s }] = await Promise.all([
        api.get(`/evidence/vault?${params.toString()}`),
        api.get("/evidence/vault/stats"),
      ]);
      setItems(v.items || []);
      setStats(s);
    } catch (e) { toast.error("Failed to load"); }
    setLoading(false);
  };

  useEffect(() => { load();   }, [module]);

  const fmt = (n) => n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <PageHeader
        kicker="Governance · Central Vault"
        title="Evidence Vault"
        description="Unified repository of every evidence file uploaded across DPDP Assessment, Company Profile, VAPT and ISO 27001 — with SHA-256 integrity fingerprinting, traceability back to the source control/finding/question, and search."
        testId="evidence-vault-page"
        actions={<Btn variant="secondary" size="sm" onClick={load} data-testid="ev-refresh"><Icons.RefreshCw className="w-3.5 h-3.5" /> Refresh</Btn>}
      />

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <div className="aurora rounded-xl p-4 col-span-2" style={{ border: "1px solid var(--border)" }}>
            <div className="label-cap mb-1">Total evidence</div>
            <div className="flex items-baseline gap-2">
              <div className="num-display text-3xl tnum">{stats.total_files}</div>
              <div className="text-[11px] muted">files · {fmt(stats.total_bytes)}</div>
            </div>
          </div>
          {Object.entries(MODULE_META).map(([k, m]) => {
            const Ico = Icons[m.icon];
            const n = stats.counts_by_module?.[k] || 0;
            const active = module === k;
            return (
              <button key={k} onClick={() => setModule(active ? "" : k)} className="rounded-xl p-4 text-left card-hover"
                style={{ border: `1px solid ${active ? m.color : "var(--border)"}`, background: active ? `${m.color}08` : "var(--surface)" }}
                data-testid={`ev-module-${k}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${m.color}18` }}>
                    <Ico className="w-3.5 h-3.5" style={{ color: m.color }} />
                  </div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: m.color }}>{m.label}</div>
                </div>
                <div className="num-display text-xl tnum" style={{ color: m.color }}>{n}</div>
              </button>
            );
          })}
        </div>
      )}

      <div className="surface rounded-2xl p-4 mb-4" style={{ border: "1px solid var(--border)" }}>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Icons.Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 muted" />
            <TextInput style={{ paddingLeft: "2rem" }} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search by label, filename, uploader..." data-testid="ev-search" />
          </div>
          <SelectInput value={module} onChange={(e) => setModule(e.target.value)} className="max-w-[200px]" data-testid="ev-module-select">
            <option value="">All modules</option>
            {Object.entries(MODULE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </SelectInput>
          <Btn onClick={load} data-testid="ev-search-btn"><Icons.Search className="w-3.5 h-3.5" /> Search</Btn>
        </div>
      </div>

      {loading ? <Loader /> : items.length === 0 ? (
        <EmptyState title="No evidence" hint="Upload files inside DPDP Assessment, Company Profile, VAPT or ISO 27001 modules." />
      ) : (
        <div className="surface rounded-2xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
          <table className="w-full text-[12px]">
            <thead style={{ background: "var(--bg-2)" }}>
              <tr>
                <th className="text-left px-3 py-2 label-cap">Module</th>
                <th className="text-left px-3 py-2 label-cap">Label</th>
                <th className="text-left px-3 py-2 label-cap">Uploaded by</th>
                <th className="text-left px-3 py-2 label-cap">Size</th>
                <th className="text-left px-3 py-2 label-cap">Uploaded</th>
                <th className="text-left px-3 py-2 label-cap">SHA-256</th>
              </tr>
            </thead>
            <tbody>
              {items.map(i => {
                const m = MODULE_META[i.module] || {};
                const Ico = Icons[m.icon] || Icons.File;
                return (
                  <tr key={i.id} className="hover:bg-app-2 transition-colors" style={{ borderTop: "1px solid var(--border)" }} data-testid={`ev-row-${i.id}`}>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 px-1.5 py-[1px] rounded text-[10.5px] font-medium" style={{ background: `${m.color}15`, color: m.color }}>
                        <Ico className="w-3 h-3" /> {m.label}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-[12.5px] font-medium ink truncate max-w-xs">{i.label}</div>
                      <div className="text-[10.5px] muted truncate max-w-xs">{i.filename} · {i.kind}</div>
                    </td>
                    <td className="px-3 py-2 muted text-[11.5px] truncate max-w-[180px]">{i.uploaded_by}</td>
                    <td className="px-3 py-2 mono text-[11px] tnum">{fmt(i.size || 0)}</td>
                    <td className="px-3 py-2 muted text-[11px] whitespace-nowrap">{(i.uploaded_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2 mono text-[10px] muted">{(i.sha256 || "").slice(0, 12)}…</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
